import { pushQueries, removeQueries } from '../../../utils';
import { ORDER_KEY, SORT_KEY } from '../../../utils/const';
import { LOCAL_STORAGE_KEYS } from './constants';
import { DataTableColumn, FilterItem, SortConfig } from './types';

export const handleApplyFilter = (
  key: string,
  value: string,
  appliedFilters: FilterItem[],
  setAppliedFilters: React.Dispatch<React.SetStateAction<FilterItem[]>>,
  tableId: string,
  callBack?: () => void,
): void => {
  if (!key || !value) return;

  const updatedFilters = updateFilters(appliedFilters, key, value);
  setAppliedFilters(updatedFilters);
  // saveFiltersToStorage(updatedFilters, tableId);
  updateUrlParams(key, value);
  callBack?.();
};


export const handleSort = (
  key: string,
  currentSort: SortConfig,
  setSort: (sort: SortConfig) => void,
  callBack?: () => void,
): void => {
  const { column, direction } = currentSort;
  let newSort: SortConfig;

  if (column === key) {
    if (direction === 'asc') newSort = { column: key, direction: 'desc' };
    else if (direction === 'desc') newSort = { column: '', direction: '' };
    else newSort = { column: key, direction: 'asc' };
  } else {
    newSort = { column: key, direction: 'asc' };
  }

  setSort(newSort);

  if (newSort.column) {
    pushQueries({ [SORT_KEY]: newSort.column, [ORDER_KEY]: newSort.direction });
  } else {
    removeQueries([SORT_KEY, ORDER_KEY]);
  }

  callBack?.();
};

export const handleToggleColumn = (
  key: string,
  columns: DataTableColumn[],
  setColumns: React.Dispatch<React.SetStateAction<DataTableColumn[]>>,
  tableId: string,
  callBack?: () => void,
): void => {
  const updatedColumns = columns.map((col) =>
    col.key === key && !col.locked ? { ...col, isHide: !col.isHide } : col,
  );

  setColumns(updatedColumns);
  saveColumnVisibility(updatedColumns, tableId);
  callBack?.();
};

export const handleReorderColumns = (
  newColumns: DataTableColumn[],
  setColumns: React.Dispatch<React.SetStateAction<DataTableColumn[]>>,
  tableId: string,
  callBack?: () => void,
): void => {
  setColumns(newColumns);
  saveColumnOrder(newColumns, tableId);
  callBack?.();
};

// Helper functions
// const saveFiltersToStorage = (filters: FilterItem[], tableId: string): void => {
//   localStorage.setItem(
//     `${tableId}${LOCAL_STORAGE_KEYS.FILTERS}`,
//     JSON.stringify(filters),
//   );
// };

const updateUrlParams = (key: string, value?: string): void => {
  value ? pushQueries({ [key]: value }) : removeQueries([key]);
};

const updateFilters = (
  currentFilters: FilterItem[],
  key: string,
  value: string,
): FilterItem[] => {
  const existingIndex = currentFilters.findIndex(
    (filter) => filter.key === key,
  );
  if (existingIndex !== -1) {
    return currentFilters.map((filter, index) =>
      index === existingIndex ? { ...filter, value } : filter,
    );
  }
  return [...currentFilters, { key, value }];
};

const saveColumnVisibility = (
  columns: DataTableColumn[],
  tableId: string,
): void => {
  const isHideMap = columns.reduce<Record<string, boolean>>((acc, col) => {
    if (col.isHide) acc[col.key] = col.isHide;
    return acc;
  }, {});
  localStorage.setItem(
    `${tableId}${LOCAL_STORAGE_KEYS.IS_HIDE}`,
    JSON.stringify(isHideMap),
  );
};

export const saveColumnOrder = (
  columns: DataTableColumn[],
  tableId: string,
): void => {
  const order = columns.map((col) => col.key);
  localStorage.setItem(
    `${tableId}${LOCAL_STORAGE_KEYS.COLUMN_ORDER}`,
    JSON.stringify(order),
  );
};

export const loadColumnOrder = (
  columns: DataTableColumn[],
  tableId: string,
): DataTableColumn[] => {
  const savedOrder = localStorage.getItem(
    `${tableId}${LOCAL_STORAGE_KEYS.COLUMN_ORDER}`,
  );
  if (!savedOrder) return columns;

  try {
    const order = JSON.parse(savedOrder) as string[];
    const columnMap = new Map(columns.map((col) => [col.key, col]));
    return order
      .map((key) => columnMap.get(key))
      .filter((col): col is DataTableColumn => col !== undefined);
  } catch {
    return columns;
  }
};

export const loadColumnVisibility = (
  columns: DataTableColumn[],
  tableId: string,
): DataTableColumn[] => {
  const savedVisibility = localStorage.getItem(`${tableId}${LOCAL_STORAGE_KEYS.IS_HIDE}`);
  if (!savedVisibility) return columns;

  try {
    const visibilityMap = JSON.parse(savedVisibility) as Record<string, boolean>;
    return columns.map((col) => ({ ...col, isHide: visibilityMap[col.key] }));
  } catch {
    return columns;
  }
};
